import * as React from "react"
import { ClassificationSource } from "@digits-graphql/frontend/graphql-bearer"
import { ViewType } from "@digits-graphql/frontend/graphql-public"
import { SvgChevronDownSolid } from "@digits-shared/components/SVGIcons/solid/ChevronDownSolid.svg"
import { DigitsButton } from "@digits-shared/DesignSystem/Button"
import {
  AiGradientDefs,
  type GradientProps,
} from "@digits-shared/DesignSystem/Containers/AIGradientDefs"
import {
  AI_GRADIENT_BORDER,
  AI_GRADIENT_TEXT,
} from "@digits-shared/DesignSystem/Containers/AIGradients"
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@digits-shared/DesignSystem/Popovers/Popover"
import stringHelper from "@digits-shared/helpers/stringHelper"
import useConstant from "@digits-shared/hooks/useConstant"
import useSession from "@digits-shared/hooks/useSession"
import useStateBoolean from "@digits-shared/hooks/useStateBoolean"
import { AspectCode } from "@digits-shared/session/AspectCode"
import colors from "@digits-shared/themes/colors"
import styled from "styled-components"
import { v4 as generateUUID } from "uuid"
import { useCategoryClassificationSummaries } from "src/frontend/components/Shared/Assistant/Agents/hooks/useClassificationSummaries"
import { useViewVersion } from "src/frontend/components/Shared/Contexts/useViewVersion"
import type FrontendSession from "src/frontend/session"

// Lazy loading agent summary to avoid pulling in the Pay graphql and other assets during the app launch
const AgentSummary = React.lazy(() =>
  import("src/frontend/components/Shared/Assistant/Agents/AgentSummary").then((mod) => ({
    default: mod.AgentSummary,
  }))
)

/*
  STYLES
*/

const AgentPill = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 8px;

  border: 1px solid ${colors.translucentWhite20};
  border-radius: 50px;

  padding: 2px 2px 2px 12px;
  font-size: 14px;
`

// TODO: Make this a real style.
const AILightButton = styled(DigitsButton)`
  background: ${colors.gradients.aiLight};

  ${AI_GRADIENT_BORDER};
  &::before {
    margin: -1.5px;
  }
`

const ButtonContent = styled.div`
  display: flex;
  align-items: center;
  gap: 6px;
  ${AI_GRADIENT_TEXT};
`

const DownArrow = styled(SvgChevronDownSolid)`
  margin-left: 4px;
  height: 8px;
  width: 8px;
`

const AIDownArrow = styled(DownArrow)<GradientProps>`
  path {
    stroke-width: 1.2;
    fill: url(#${({ uuid }) => uuid});
    stroke: url(#${({ uuid }) => uuid}) !important;
  }
`

/*
  COMPONENT
*/

export const AgentSummaryButton: React.FC = () => {
  const iconUuid = useConstant(generateUUID())
  const { viewType } = useViewVersion()
  const { hasAccessToAspect, currentOrganization } = useSession<FrontendSession>()
  const { value: isShowing, setValue: onOpenChange, setFalse: hide } = useStateBoolean(false)

  const hasBookkeepingAgent =
    viewType === ViewType.AIBookkeeper && !hasAccessToAspect(AspectCode.AskDigits)

  const { hoursSaved: currentHoursSaved, loading } = useCategoryClassificationSummaries(
    ClassificationSource.Digits,
    !hasBookkeepingAgent
  )

  // Show the prior period's value during loading to preven the UI from flickering.
  const prevHoursSavedRef = React.useRef<number | undefined>(undefined)
  if (!loading) {
    prevHoursSavedRef.current = currentHoursSaved
  }
  const hoursSaved = loading ? prevHoursSavedRef.current : currentHoursSaved
  const isDemoOrg = currentOrganization?.isDemo

  if (!hasBookkeepingAgent || (loading && prevHoursSavedRef.current === undefined)) {
    return null
  }

  return (
    <Popover open={isShowing} onOpenChange={onOpenChange}>
      <PopoverTrigger asChild>
        {!isDemoOrg && hoursSaved ? (
          <AgentPill>
            Time Saved
            <AILightButton size="small" $variant="secondary-light" aria-label="Agents">
              <ButtonContent>
                {hoursSaved} {stringHelper.pluralize(hoursSaved, "hour", "hours")}
                <AIDownArrow uuid={iconUuid}>
                  <AiGradientDefs uuid={iconUuid} />
                </AIDownArrow>
              </ButtonContent>
            </AILightButton>
          </AgentPill>
        ) : (
          <DigitsButton size="medium" $variant="secondary-white" aria-label="Agents" $solidIcon>
            Agents
            <DownArrow />
          </DigitsButton>
        )}
      </PopoverTrigger>

      <PopoverContent
        css="background: transparent; backdrop-filter: blur(15px); padding: 0; border-radius: 16px;"
        showCloseButton={false}
        side="top"
        align="start"
      >
        <React.Suspense>
          <AgentSummary onClose={hide} />
        </React.Suspense>
      </PopoverContent>
    </Popover>
  )
}
